// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0031-shared-wizard-settings-editors.md

import * as React from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ORG_DISPLAY_NAME_MAX_LENGTH as MAX_DISPLAY_NAME_LENGTH,
  ORG_DISPLAY_NAME_MIN_LENGTH as MIN_DISPLAY_NAME_LENGTH,
} from '@greenlight/shared';

function validationError(value: string): string | null {
  const length = value.trim().length;
  return length >= MIN_DISPLAY_NAME_LENGTH && length <= MAX_DISPLAY_NAME_LENGTH
    ? null
    : `Display name must be between ${MIN_DISPLAY_NAME_LENGTH} and ${MAX_DISPLAY_NAME_LENGTH} characters.`;
}

export interface DisplayNameEditorState {
  displayName: string;
  setDisplayName: (value: string) => void;
  trimmedDisplayName: string;
  error: string | null;
  isDirty: boolean;
  isValid: boolean;
  validate: () => string | null;
  reset: () => void;
}

export function useDisplayNameEditor(initialValue: string): DisplayNameEditorState {
  const [displayName, setDisplayName] = React.useState(initialValue);
  const [validated, setValidated] = React.useState(false);

  React.useEffect(() => {
    setDisplayName(initialValue);
    setValidated(false);
  }, [initialValue]);

  const trimmedDisplayName = displayName.trim();
  const currentError = validationError(displayName);

  return {
    displayName,
    setDisplayName,
    trimmedDisplayName,
    error: validated ? currentError : null,
    isDirty: trimmedDisplayName !== initialValue.trim(),
    isValid: currentError === null,
    validate: () => {
      setValidated(true);
      return currentError === null ? trimmedDisplayName : null;
    },
    reset: () => {
      setDisplayName(initialValue);
      setValidated(false);
    },
  };
}

export interface DisplayNameEditorProps {
  editor: DisplayNameEditorState;
  id?: string;
  inputTestId?: string;
  disabled?: boolean;
  description?: string;
}

export function DisplayNameEditor({
  editor,
  id: idProp,
  inputTestId,
  disabled = false,
  description = 'Shown in the dashboard and agent setup.',
}: DisplayNameEditorProps): React.JSX.Element {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>Display name</Label>
      <Input
        id={id}
        value={editor.displayName}
        onChange={(event) => editor.setDisplayName(event.target.value)}
        onBlur={() => editor.validate()}
        disabled={disabled}
        required
        minLength={MIN_DISPLAY_NAME_LENGTH}
        maxLength={MAX_DISPLAY_NAME_LENGTH}
        aria-invalid={editor.error !== null}
        aria-describedby={editor.error ? errorId : undefined}
        data-testid={inputTestId}
      />
      {editor.error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {editor.error}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
}
